// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
'use client';

/* ═══════════════════════════════════════════════════════════════════════════
 * LA PASTILLE « LIVRET » — expliquer la norme là où l'on agit, dans les 3 modules
 *
 * ── Pourquoi un second composant, alors que Qualité en a déjà un ───────────
 *
 * `ged/src/components/Norme.tsx` fait davantage : il navigue vers l'écran de
 * correspondance, ouvre la fiche du document externe au fonds, liste les écrans
 * concernés. Tout cela passe par `useGed` — le magasin du module Qualité, qui
 * n'existe ni sur le Site ni dans le Manuel.
 *
 * Or la commande la plus utilisée du système — « Déclarer une NC » — est
 * justement rendue dans les TROIS modules par la gommette partagée. Y importer
 * le composant de Qualité aurait cassé les deux autres.
 *
 * Celui-ci ne dépend que de `iso15189.ts`, qui est déjà dans le socle partagé.
 * Il dit la même chose ; il ne va nulle part. ⚠️ Ne pas y ajouter de navigation :
 * ce serait redevenir le composant de Qualité, et rompre les deux autres modules.
 *
 * ── Ce qu'il affiche, et l'ordre des rubriques ─────────────────────────────
 *
 * Trois rubriques, pas quatre. « Comment BIO24 y répond » est retiré ici : la
 * pastille s'ouvre AU MOMENT où l'on remplit le formulaire, et décrire à cet
 * instant le dispositif que l'on est en train d'utiliser n'apprend rien. Restent
 * ce que la norme demande, pourquoi, et ce qu'un auditeur viendra vérifier.
 *
 * ⚠️ AUCUN TEXTE NORMATIF. L'ISO 15189 est protégée ; ce panneau porte la
 * RÉFÉRENCE et la reformulation du laboratoire, jamais la clause. Même règle
 * que `shared/ui/iso15189.ts`, dont il ne fait que lire les champs.
 * ═══════════════════════════════════════════════════════════════════════════ */

import React from 'react';
import { clause, NUMEROTATION_VERIFIEE } from './iso15189';

const RUBRIQUES: { cle: 'exigence' | 'pourquoi' | 'preuve'; titre: string }[] = [
  { cle: 'exigence', titre: 'Ce que la norme demande' },
  { cle: 'pourquoi', titre: 'Pourquoi cette exigence existe' },
  { cle: 'preuve', titre: 'Ce que l’auditeur demandera' },
];

/** Le gras `**…**` des textes du référentiel, rendu plutôt qu'affiché tel quel. */
function Texte({ v }: { v: string }) {
  return (
    <>
      {v.split(/(\*\*[^*]+\*\*)/g).map((bout, i) =>
        bout.startsWith('**') && bout.endsWith('**')
          ? <strong key={i}>{bout.slice(2, -2)}</strong>
          : <React.Fragment key={i}>{bout}</React.Fragment>)}
    </>
  );
}

export function PastilleNorme({ art }: { art: string }) {
  const [ouvert, setOuvert] = React.useState(false);
  const c = clause(art);
  /* Une pastille sur une clause absente du référentiel serait un lien mort :
     mieux vaut ne rien afficher que promettre une explication inexistante. */
  if (!c) return null;

  return (
    <span style={{ position: 'relative', display: 'inline-flex' }}>
      <button type="button" onClick={() => setOuvert((v) => !v)}
        aria-expanded={ouvert}
        title={`ISO 15189 §${c.ref} — ${c.titre}. Cliquez pour l’explication.`}
        aria-label={`Explication de l’article ${c.ref} — ${c.titre}`}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: ouvert ? 'var(--cyan-100, #CFF0F7)' : 'var(--cyan-50, #ECFAFD)',
          color: 'var(--cyan-700, #0B6E85)', border: '1px solid var(--cyan-200, #A9E2EE)',
          borderRadius: 999, padding: '1px 8px', cursor: 'pointer',
          font: 'inherit', fontFamily: 'var(--font-mono, monospace)', fontSize: 10.5, fontWeight: 700,
        }}>
        {/* Le glyphe du livret, dessiné ici : le socle partagé n'embarque pas la
            fonte d'icônes du module Qualité. */}
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor"
          strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
        §{c.ref}
      </button>

      {ouvert && (
        <span role="dialog" aria-label={`ISO 15189 §${c.ref}`}
          /* ⚠️ `position: absolute` et non `fixed` : la pastille est rendue DANS
             une fenêtre modale, et un panneau en `fixed` se placerait par-dessus
             elle, en couvrant le formulaire qu'on est en train de remplir. */
          style={{
            position: 'absolute', top: 'calc(100% + 6px)', insetInlineStart: 0, zIndex: 5,
            width: 'min(340px, 78vw)', display: 'grid', gap: 10, textAlign: 'left',
            background: 'var(--surface-card, #fff)', border: '1px solid var(--border-default, #E4E7EC)',
            borderRadius: 10, boxShadow: '0 12px 34px rgba(14,39,64,0.18)', padding: 14,
            font: 'inherit', fontFamily: 'var(--font-sans, system-ui)', fontWeight: 400,
            textTransform: 'none', letterSpacing: 'normal',
          }}>
          <span style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
            <span style={{ flex: 1 }}>
              <span style={{ display: 'block', fontFamily: 'var(--font-mono, monospace)', fontSize: 11.5, fontWeight: 700, color: 'var(--cyan-700, #0B6E85)' }}>
                ISO 15189:2022 · §{c.ref}
              </span>
              <span style={{ display: 'block', fontSize: 14, fontWeight: 700, color: 'var(--text-strong, #101828)', marginTop: 2 }}>{c.titre}</span>
            </span>
            <button type="button" onClick={() => setOuvert(false)} aria-label="Fermer"
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted, #667085)', font: 'inherit', fontSize: 15, lineHeight: 1, padding: 2 }}>×</button>
          </span>

          {(!c.valide || !NUMEROTATION_VERIFIEE) && (
            <span style={{ display: 'block', background: 'var(--amber-50, #FFF8EC)', color: 'var(--amber-600, #8A5B00)', borderRadius: 7, padding: '7px 9px', fontSize: 11.5, lineHeight: 1.5 }}>
              Rédaction proposée, <strong>non validée par le responsable qualité</strong>
              {!NUMEROTATION_VERIFIEE && ' ; la numérotation reste à confronter à l’exemplaire du laboratoire'}.
            </span>
          )}

          {RUBRIQUES.map(({ cle, titre }) => (
            <span key={cle} style={{ display: 'block' }}>
              <span style={{ display: 'block', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-muted, #667085)', marginBottom: 3 }}>{titre}</span>
              <span style={{ display: 'block', fontSize: 12.5, lineHeight: 1.55, color: 'var(--text-body, #344054)' }}><Texte v={c[cle]} /></span>
            </span>
          ))}

          <span style={{ display: 'block', fontSize: 11, lineHeight: 1.5, color: 'var(--text-muted, #667085)', borderTop: '1px dashed var(--border-default, #E4E7EC)', paddingTop: 8 }}>
            Le texte de la norme n’est pas reproduit : l’ISO 15189 est vendue sous licence.
            L’exemplaire du laboratoire reste la seule source opposable.
          </span>
        </span>
      )}
    </span>
  );
}
